import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { ArrowLeft } from 'lucide-react';
import ReactModal from 'react-modal';

import { fetchDirectoryMembers, searchDirectoryAccounts } from '../api';
import { DirectoryAccount, DirectoryMember } from '../types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  // Rejects when the send failed; the caller has already said so, and the
  // modal keeps the draft.
  onStart: (accountId: string, userId: string, body: string) => Promise<void>;
};

const memberLabel = (member: DirectoryMember): string =>
  member.full_name || member.username || member.email || member.user_id;

/**
 * Support writing first: find the account, pick one of its users, say
 * something. Searches cosmos's accounts rather than the inbox's contacts
 * because the people worth reaching this way are mostly ones who have never
 * opened the chat, and those aren't contacts yet.
 */
const NewConversationModal: React.FC<Props> = ({ isOpen, onClose, onStart }: Props) => {
  const [query, setQuery] = useState('');
  const [accounts, setAccounts] = useState<DirectoryAccount[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [account, setAccount] = useState<DirectoryAccount | null>(null);
  const [members, setMembers] = useState<DirectoryMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [member, setMember] = useState<DirectoryMember | null>(null);
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) return;
    // Every opening starts clean: a half-picked person from the last time is
    // the wrong person to find preselected.
    setQuery('');
    setAccounts([]);
    setAccount(null);
    setMembers([]);
    setMember(null);
    setBody('');
    setFailure(null);
  }, [isOpen]);

  useEffect(() => {
    const term = query.trim();
    if (!isOpen || account || !term) {
      setAccounts([]);
      setIsSearching(false);
      return undefined;
    }
    // Debounced, and guarded so a slow answer for "cli" can't paint over the
    // one for "clinica".
    let active = true;
    setIsSearching(true);
    const handle = window.setTimeout(() => {
      searchDirectoryAccounts(term)
        .then(({ data }) => {
          if (!active) return;
          setAccounts(data);
          setFailure(null);
        })
        .catch(() => {
          if (active) setFailure('Could not search accounts.');
        })
        .finally(() => {
          if (active) setIsSearching(false);
        });
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [isOpen, account, query]);

  useEffect(() => {
    setMembers([]);
    setMember(null);
    if (!account) return undefined;
    let active = true;
    setIsLoadingMembers(true);
    fetchDirectoryMembers(account.id)
      .then(({ data }) => {
        if (active) setMembers(data);
      })
      .catch(() => {
        if (active) setFailure('Could not load the users of that account.');
      })
      .finally(() => {
        if (active) setIsLoadingMembers(false);
      });
    return () => {
      active = false;
    };
  }, [account]);

  const send = async (): Promise<void> => {
    if (!account || !member || !body.trim() || isSending) return;
    setIsSending(true);
    try {
      await onStart(account.id, member.user_id, body.trim());
    } catch {
      // Keep the draft: the caller already told them it didn't go through.
    } finally {
      setIsSending(false);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal"
      overlayClassName="overlay"
      appElement={document.querySelector('#root') as HTMLElement}
    >
      <h2 className="text-lg font-semibold text-black-1.0">New conversation</h2>
      <p className="mt-1 text-sm text-gray-1.0">
        Write to any user of an account, even if they have never used the chat. They see it in the
        support widget, unread, the next time they open BrainHi.
      </p>

      {!account ? (
        <div className="mt-4">
          <label className="sr-only" htmlFor="new-conversation-account">
            Search accounts
          </label>
          <input
            id="new-conversation-account"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Account name, email, phone or id…"
            className="w-full px-3 py-2 border rounded border-white-3.0 focus:outline-none"
          />
          <ul
            className="mt-2 overflow-y-auto max-h-64 divide-y divide-white-1.0"
            aria-live="polite"
          >
            {isSearching && <li className="py-2 text-sm text-gray-1.0">Searching…</li>}
            {!isSearching && query.trim() && accounts.length === 0 && !failure && (
              <li className="py-2 text-sm text-gray-1.0">No accounts match.</li>
            )}
            {accounts.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  className="w-full px-2 py-2 text-sm text-left rounded text-black-1.0 hover:bg-white-1.0"
                  onClick={() => {
                    setFailure(null);
                    setAccount(option);
                  }}
                >
                  {option.name || option.id}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Change account"
              className="p-1 rounded text-gray-2.0 hover:bg-white-1.0"
              onClick={() => {
                setFailure(null);
                setAccount(null);
              }}
            >
              <ArrowLeft size={16} />
            </button>
            <p className="text-sm font-medium truncate text-black-1.0">
              {account.name || account.id}
            </p>
          </div>

          <ul
            className="mt-2 overflow-y-auto max-h-48 divide-y divide-white-1.0"
            aria-label="Users"
            aria-live="polite"
          >
            {isLoadingMembers && <li className="py-2 text-sm text-gray-1.0">Loading users…</li>}
            {!isLoadingMembers && members.length === 0 && !failure && (
              <li className="py-2 text-sm text-gray-1.0">This account has no users.</li>
            )}
            {members.map((option) => (
              <li key={option.user_id}>
                <button
                  type="button"
                  disabled={!option.is_active}
                  aria-pressed={member?.user_id === option.user_id}
                  className={classNames(
                    'w-full px-2 py-2 text-left rounded disabled:opacity-50 disabled:cursor-not-allowed',
                    member?.user_id === option.user_id ? 'bg-white-2.0' : 'hover:bg-white-1.0'
                  )}
                  onClick={() => setMember(option)}
                >
                  <span className="block text-sm truncate text-black-1.0">
                    {memberLabel(option)}
                    {!option.is_active && (
                      <span className="ml-2 text-xs text-gray-1.0">deactivated</span>
                    )}
                  </span>
                  {option.email && (
                    <span className="block text-xs truncate text-gray-1.0">{option.email}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          {member && (
            <>
              <label className="sr-only" htmlFor="new-conversation-body">
                Message
              </label>
              <textarea
                id="new-conversation-body"
                value={body}
                maxLength={10_000}
                rows={4}
                onChange={(event) => setBody(event.target.value)}
                placeholder={`Write to ${memberLabel(member)}…`}
                className="w-full px-3 py-2 mt-3 border rounded resize-none border-white-3.0 focus:outline-none"
              />
            </>
          )}
        </div>
      )}

      {failure && (
        <p role="alert" className="mt-2 text-sm text-red-1.0">
          {failure}
        </p>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          className="px-3 py-1 text-sm rounded text-gray-2.0 hover:bg-white-1.0"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!member || !body.trim() || isSending}
          className="px-4 py-1 text-sm rounded bg-primary-2.0 text-white-0.0 disabled:opacity-50"
          onClick={() => void send()}
        >
          {isSending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </ReactModal>
  );
};

export default NewConversationModal;
